/**
 * S13-F001 - Scheduling Console Map view.
 *
 * Renders the window's inspections on a custom-styled ("Clara Vale")
 * Google map: warm cream/ink cartography so it reads as native to the
 * platform, markers in each status's workflow colour (as on the calendar's
 * cards), and a floating selected-inspection panel + legend.
 *
 * Coordinates come from the server-side geocode endpoint
 * (`useGeocodeInspections`) - Inspection stores text addresses only.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps'
import { MapPin, Route, X } from 'lucide-react'

import {
  useGeocodeInspections,
  useOptimizeRoute,
  useInspectionChoices,
  useLifecycleStatusColours,
  type GeocodeResult,
  type InspectionRow,
  type RouteOptimizationResult,
} from '@/admin/hooks/use-inspections'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { formatDistanceMeters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID ?? ''

function buildLabelMap(options: { value: string; label: string }[] | undefined): Record<string, string> {
  if (!options) return {}
  return Object.fromEntries(options.map((o) => [o.value, o.label]))
}

/**
 * A pin or legend dot in its status's workflow state colour. The colour is
 * data from the API, so it is set at runtime; a status no workflow colours
 * keeps the muted default fill.
 */
function fillStyle(colour: string | undefined) {
  return colour ? { backgroundColor: colour } : undefined
}

function FitBounds({
  points,
}: {
  points: { lat: number; lng: number }[]
}) {
  const map = useMap()
  useEffect(() => {
    if (!map || points.length === 0) return
    if (points.length === 1) {
      map.setCenter(points[0])
      map.setZoom(13)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    points.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, 64)
  }, [map, points])
  return null
}

// The optimised path drawn between stops, in visiting order. @vis.gl
// 1.x has no <Polyline>, so this draws imperatively on the map
// instance and tears down on change/unmount. Ink stroke (literal - the
// Maps API needs a colour string, not a CSS var) keeps it on-brand.
function RoutePath({
  path,
}: {
  path: { lat: number; lng: number }[]
}) {
  const map = useMap()
  const key = path.map((p) => `${p.lat},${p.lng}`).join('|')
  useEffect(() => {
    if (!map || path.length < 2) return
    const line = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#3a3a36',
      strokeOpacity: 0.7,
      strokeWeight: 3,
    })
    line.setMap(map)
    return () => line.setMap(null)
    // `key` captures the ordered coords; path identity alone churns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key])
  return null
}

// Marker pin rendered as DOM inside AdvancedMarker. The status colour drives
// the fill (the same one the legend shows), routed markers carry their
// 1-based visiting-order number.
function MarkerPin({
  colour,
  selected,
  ordinal,
}: {
  colour: string | undefined
  selected: boolean
  ordinal: number | undefined
}) {
  const routed = ordinal != null
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full border-[var(--primary-foreground)] bg-[var(--ink-3)]',
        routed
          ? 'h-[26px] w-[26px]'
          : selected
            ? 'h-5 w-5'
            : 'h-3.5 w-3.5',
        selected ? 'border-[3px]' : 'border-2',
      )}
      style={fillStyle(colour)}
    >
      {routed && (
        <span className="text-[11px] font-semibold leading-none text-[var(--primary-foreground)]">
          {ordinal}
        </span>
      )}
    </div>
  )
}

/** Drive time as "1h 03m" / "47m" (distance is locale-formatted separately). */
function formatRouteTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}

/** Local-zone YYYY-MM-DD for an inspection's scheduled_for, or null. */
function dayKeyOf(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function SchedulingMapView({
  inspections,
  isLoading,
  onSelect,
  initialDay = '',
}: {
  inspections: InspectionRow[]
  isLoading: boolean
  onSelect: (uuid: string) => void
  /**
   * When the user jumps here from a specific calendar day (Phase 0a),
   * the map opens scoped to that YYYY-MM-DD. Empty = whole window.
   */
  initialDay?: string
}) {
  const { t } = useTranslation()
  const { data: choices } = useInspectionChoices()
  const statusLabels = useMemo(() => buildLabelMap(choices?.lifecycle_statuses), [choices])
  const statusColours = useLifecycleStatusColours()
  // The legend names the statuses on the map, in the tenant's order.
  const legendStatuses = useMemo(() => {
    const shown = new Set(inspections.map((row) => row.lifecycle_status))
    return (choices?.lifecycle_statuses ?? []).filter((s) => shown.has(s.value))
  }, [choices, inspections])
  const geocode = useGeocodeInspections()
  const [result, setResult] = useState<GeocodeResult>({
    resolved: {},
    unresolved: [],
  })
  const [selected, setSelected] = useState<string | null>(null)
  const requestedKey = useRef<string>('')

  // Day scope: '' = the whole window; otherwise a YYYY-MM-DD key. A
  // route is per-day (an FTS doesn't drive one path across a month) -
  // mirrors the Week calendar's per-day optimise.
  const [day, setDay] = useState<string>(initialDay)
  // If a later jump targets a different day while this view is mounted,
  // re-scope to it. A blank initialDay never clobbers a manual pick.
  useEffect(() => {
    if (initialDay) setDay(initialDay)
  }, [initialDay])
  const scoped = useMemo(
    () =>
      day
        ? inspections.filter((i) => dayKeyOf(i.scheduled_for) === day)
        : inspections,
    [day, inspections],
  )

  const uuids = useMemo(
    () => scoped.map((i) => i.uuid).sort().join(','),
    [scoped],
  )

  useEffect(() => {
    if (!scoped.length || requestedKey.current === uuids) return
    requestedKey.current = uuids
    geocode.mutate(
      scoped.map((i) => i.uuid),
      { onSuccess: (r) => setResult(r) },
    )
    // geocode is stable from react-query; uuids drives re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuids, scoped])

  const byUuid = useMemo(
    () => Object.fromEntries(scoped.map((i) => [i.uuid, i])),
    [scoped],
  )
  const points = useMemo(
    () =>
      Object.entries(result.resolved)
        .filter(([uuid]) => byUuid[uuid])
        .map(([uuid, p]) => ({ uuid, lat: p.lat, lng: p.lng })),
    [result, byUuid],
  )
  const selectedRow = selected ? byUuid[selected] : null
  const selectedPoint = selected ? result.resolved[selected] : null

  const { locale } = useFormatting()
  const optimize = useOptimizeRoute()
  const [route, setRoute] = useState<RouteOptimizationResult | null>(null)
  const [routeErr, setRouteErr] = useState<string | null>(null)

  // Days present in the window (from the full set, so any day is
  // pickable even while scoped), each with its count.
  const dayOptions = useMemo(() => {
    // NB: `Map` is the @vis.gl component import in this file - use a
    // plain record, not the shadowed global Map constructor.
    const counts: Record<string, number> = {}
    for (const i of inspections) {
      const k = dayKeyOf(i.scheduled_for)
      if (k) counts[k] = (counts[k] ?? 0) + 1
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({
        key,
        count,
        label: new Date(`${key}T00:00:00`).toLocaleDateString(locale, {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
        }),
      }))
  }, [inspections, locale])

  // A new inspection window invalidates any drawn route.
  useEffect(() => {
    setRoute(null)
    setRouteErr(null)
  }, [uuids])

  // uuid → 1-based visiting position (only when a route is drawn).
  // NB: `Map` here is the @vis.gl component import - use a plain
  // record, not the (shadowed) global Map constructor.
  const orderByUuid = useMemo(() => {
    const m: Record<string, number> = {}
    route?.ordered_inspection_uuids.forEach((u, i) => {
      m[u] = i + 1
    })
    return m
  }, [route])

  // The polyline path: resolved coords walked in visiting order.
  const routedPath = useMemo(() => {
    if (!route) return []
    return route.ordered_inspection_uuids
      .map((u) => result.resolved[u])
      .filter((p): p is NonNullable<typeof p> => p != null)
      .map((p) => ({ lat: p.lat, lng: p.lng }))
  }, [route, result])

  function handleOptimise() {
    setRouteErr(null)
    optimize.mutate(
      points.map((p) => p.uuid),
      {
        onSuccess: (r) => setRoute(r),
        onError: (err: unknown) => {
          const status = (
            err as { response?: { status?: number } } | undefined
          )?.response?.status
          setRouteErr(
            status === 503
              ? t(
                  'inspections.map.routeUnavailableEnv',
                  "Route optimisation isn't configured for this environment.",
                )
              : t(
                  'inspections.map.routeUnavailable',
                  'Route service is temporarily unavailable.',
                ),
          )
        },
      },
    )
  }

  if (!MAPS_KEY || !MAP_ID) {
    const missing = !MAPS_KEY ? 'VITE_GOOGLE_MAPS_API_KEY' : 'VITE_GOOGLE_MAPS_MAP_ID'
    return (
      <div className="mt-6 flex flex-col items-center justify-center gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] py-16 text-center">
        <MapPin size={20} className="text-[var(--ink-3)]" />
        <p className="text-sm text-[var(--ink-2)]">
          {t(
            'inspections.map.mapUnavailable',
            'Map unavailable - Google Maps not fully configured for this environment.',
          )}
        </p>
        <p className="text-[11px] text-[var(--ink-3)]">
          {t('inspections.map.missingConfigPrefix', 'Set')}{' '}
          <span className="font-mono">{missing}</span>{' '}
          {t('inspections.map.missingConfigSuffix', 'to enable the Map view.')}
        </p>
      </div>
    )
  }

  const mapping = isLoading || geocode.isPending
  const unresolvedCount = result.unresolved.length

  return (
    <div className="panel-arrive relative mt-6 overflow-hidden rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] shadow-[var(--shadow-float)]">
      <div className="h-[640px] w-full">
        <APIProvider apiKey={MAPS_KEY}>
          <Map
            mapId={MAP_ID}
            defaultCenter={{ lat: 39.5, lng: -98.35 }}
            defaultZoom={4}
            disableDefaultUI
            zoomControl
            gestureHandling="greedy"
            clickableIcons={false}
          >
            <FitBounds points={points.map((p) => ({ lat: p.lat, lng: p.lng }))} />
            {routedPath.length >= 2 && <RoutePath path={routedPath} />}
            {points.map((p) => {
              const row = byUuid[p.uuid]
              if (!row) return null
              const ordinal = orderByUuid[p.uuid]
              return (
                <AdvancedMarker
                  key={p.uuid}
                  position={{ lat: p.lat, lng: p.lng }}
                  onClick={() => setSelected(p.uuid)}
                  title={
                    ordinal != null
                      ? `${ordinal}. ${row.code} · ${row.sub_type_label}`
                      : `${row.code} · ${row.sub_type_label}`
                  }
                >
                  <MarkerPin
                    colour={statusColours[row.lifecycle_status]}
                    selected={selected === p.uuid}
                    ordinal={ordinal}
                  />
                </AdvancedMarker>
              )
            })}
          </Map>
        </APIProvider>
      </div>

      {/* Legend - floating, on-brand */}
      <div className="absolute left-4 top-4 flex flex-col gap-2 rounded-md border border-[var(--rule)] bg-[var(--bg-light)] px-3.5 py-3 shadow-[var(--shadow-float)] backdrop-blur-sm">
        <span className="font-display text-[15px] leading-none text-[var(--ink)]">
          {t('inspections.map.heading', 'Inspection map')}
        </span>
        {dayOptions.length > 0 && (
          <Select
            value={day || 'all'}
            onValueChange={(v) => setDay(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="h-8 w-[170px]">
              <SelectValue placeholder={t('inspections.map.dayPlaceholder', 'Day')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('inspections.map.allDays', 'All days ({{count}})', {
                  count: inspections.length,
                })}
              </SelectItem>
              {dayOptions.map((d) => (
                <SelectItem key={d.key} value={d.key}>
                  {t('inspections.map.dayOption', '{{label}} ({{count}})', {
                    label: d.label,
                    count: d.count,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="mt-0.5 flex flex-col gap-1.5">
          {legendStatuses.map((s) => (
            <div key={s.value} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--ink-3)] ring-2 ring-[var(--bg-light)]"
                style={fillStyle(s.color)}
              />
              <span className="text-[11px] text-[var(--ink-2)]">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status line */}
      <div className="absolute bottom-4 left-4 rounded-md border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-1.5 text-[11px] text-[var(--ink-3)] shadow-[var(--shadow-float)]">
        {mapping
          ? t('inspections.map.mapping', 'Mapping inspections…')
          : t('inspections.map.mappedCount', '{{count}} mapped', {
              count: points.length,
            }) +
            (unresolvedCount
              ? ` · ${t(
                  'inspections.map.unmappedCount',
                  '{{count}} unmapped (no usable address)',
                  { count: unresolvedCount },
                )}`
              : '')}
      </div>

      {/* Route optimiser - top-centre so it's visible the moment the
          Map tab opens (a 640px map pushes a bottom control below the
          fold). Clear of the legend (top-left) + selected card
          (top-right) + status line (bottom-left). */}
      {!mapping && points.length >= 2 && (
        <div className="absolute left-1/2 top-4 flex max-w-[300px] -translate-x-1/2 flex-col items-center gap-1.5">
          <Button
            size="sm"
            disabled={optimize.isPending}
            onClick={handleOptimise}
            className="shadow-[var(--shadow-float)]"
          >
            <Route size={13} className="mr-1.5" />
            {optimize.isPending
              ? t('inspections.map.optimising', 'Optimising…')
              : route
                ? t('inspections.map.reOptimiseRoute', 'Re-optimise route')
                : t('inspections.map.optimiseRoute', 'Optimise route')}
          </Button>
          {route && (
            <div className="flex flex-col gap-0.5 rounded-md border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-1.5 text-center shadow-[var(--shadow-float)]">
              <span className="text-[11px] font-medium text-[var(--ink)]">
                {route.distance_meters > 0 || route.duration_seconds > 0
                  ? `${formatDistanceMeters(route.distance_meters, locale)} · ${formatRouteTime(route.duration_seconds)}`
                  : t('inspections.map.visitingOrderSet', 'Visiting order set')}
              </span>
              <span className="text-[10px] text-[var(--ink-3)]">
                {t('inspections.map.stopCount', {
                  count: route.ordered_inspection_uuids.length,
                  defaultValue_one: '{{count}} stop',
                  defaultValue_other: '{{count}} stops',
                })}
                {route.days.length > 1
                  ? ` · ${t('inspections.map.daysCount', '{{count}} days', {
                      count: route.days.length,
                    })}`
                  : ''}
                {route.skipped_uuids.length > 0
                  ? ` · ${t('inspections.map.skippedCount', '{{count}} skipped', {
                      count: route.skipped_uuids.length,
                    })}`
                  : ''}
                {route.chunked ? ` · ${t('inspections.map.chunked', 'chunked')}` : ''}
              </span>
            </div>
          )}
          {routeErr && (
            <div className="rounded-md border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-1.5 text-center text-[10px] text-[var(--ink-3)] shadow-[var(--shadow-float)]">
              {routeErr}
            </div>
          )}
        </div>
      )}

      {/* Selected inspection card */}
      {selectedRow && selectedPoint && (
        <div className="panel-arrive absolute right-4 top-4 w-[300px] rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-4 shadow-[var(--shadow-float)]">
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-[10px] text-[var(--ink-3)]">
              {selectedRow.code}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setSelected(null)}
              aria-label={t('inspections.map.close', 'Close')}
            >
              <X size={13} />
            </Button>
          </div>
          <p className="mt-1 font-display text-[19px] leading-tight text-[var(--ink)]">
            {selectedRow.sub_type_label}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="secondary">
              {statusLabels[selectedRow.lifecycle_status] ??
                selectedRow.lifecycle_status.replace(/_/g, ' ')}
            </Badge>
            {selectedRow.scheduled_for && (
              <span className="text-[11px] text-[var(--ink-3)]">
                {new Date(selectedRow.scheduled_for).toLocaleDateString(
                  undefined,
                  { day: '2-digit', month: 'short' },
                )}
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-col gap-1.5 border-t border-[var(--rule)] pt-3">
            <p className="flex items-start gap-1.5 text-[12px] text-[var(--ink-2)]">
              <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--ink-3)]" />
              {selectedPoint.formatted_address}
            </p>
            {selectedRow.contractor_name && (
              <p className="text-[12px] text-[var(--ink-2)]">
                {selectedRow.contractor_name}
              </p>
            )}
            {selectedRow.inspector_name && (
              <p className="text-[11px] text-[var(--ink-3)]">
                {t('inspections.map.ftsLabel', 'FTS: {{name}}', {
                  name: selectedRow.inspector_name,
                })}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => onSelect(selectedRow.uuid)}
          >
            {t('inspections.map.openInspection', 'Open inspection')}
          </Button>
        </div>
      )}
    </div>
  )
}
